import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api-client';
import type { SystemRole, User } from '../types/api';

export const adminUsersKeys = {
  all: ['admin-users'] as const,
  list: (filters: AdminUsersFilters) => [...adminUsersKeys.all, 'list', filters] as const,
  byId: (id: string) => [...adminUsersKeys.all, 'byId', id] as const,
};

export interface AdminUsersFilters {
  q?: string | undefined;
  system_role?: SystemRole | undefined;
  is_active?: boolean | undefined;
  include_deleted?: boolean | undefined;
  limit?: number | undefined;
  offset?: number | undefined;
}

export function useAdminUsersList(filters: AdminUsersFilters = {}) {
  return useQuery({
    queryKey: adminUsersKeys.list(filters),
    queryFn: async () => {
      const res = await api.get<{ users: User[]; total: number }>('/admin/users', { ...filters });
      return res;
    },
  });
}

export interface CreateAdminUserInput {
  username: string;
  email: string;
  system_role?: SystemRole;
}

export function useCreateAdminUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateAdminUserInput) =>
      api.post<{ user: User; temp_password: string }>('/admin/users', input),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

export interface PatchAdminUserInput {
  username?: string;
  email?: string;
  system_role?: SystemRole;
}

export function useUpdateAdminUser(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: PatchAdminUserInput) =>
      api.patch<{ user: User }>(`/admin/users/${id}`, patch),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

export function useDeactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ user: User }>(`/admin/users/${id}/deactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

export function useReactivateUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.post<{ user: User }>(`/admin/users/${id}/reactivate`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

export function useAdminResetPassword() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      api.post<{ user: User; temp_password: string }>(`/admin/users/${id}/reset-password`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}

export function useSoftDeleteUser() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => api.delete(`/admin/users/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminUsersKeys.all }),
  });
}
